import React from 'react';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';
import { BentoGrid, BentoGridItem } from '@/components/lib/aceternity/BentoGrid';
import { MagicCard } from '@/components/lib/magicui/MagicCard';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface Transformation {
  id: string;
  title: string;
  before: string;
  after: string;
  span?: string;
}

const TRANSFORMATIONS: Transformation[] = [
  {
    id: 'branding-agency',
    title: 'Boutique branding agency on a single strong domain',
    before: 'Before: generic name, scattered URLs, and a site that looked like a side project.',
    after: 'After: premium .com domain, clear positioning, and a focused site that made it easier to quote higher fees.',
  },
  {
    id: 'law-firm',
    title: 'Specialist law firm moving to an authority domain',
    before: 'Before: confusing, hard-to-spell URL and a brochure site that didn\'t match the caliber of clients they wanted.',
    after: 'After: memorable authority domain, stronger brand, and a site that finally felt like their real firm.',
  },
  {
    id: 'fractional-cmo',
    title: 'Fractional CMO consolidating under one brand',
    before: 'Before: multiple domains, mixed offers, and conversations starting with "So what exactly do you do?"',
    after: 'After: one sharp domain, one clear narrative, and a website that leads with their most valuable offer.',
    span: 'md:col-span-2',
  },
];

export function HomeTransformationsAndTestimonials() {
  const headingReveal = useRevealOnce({ amount: 0.25 });
  const transformationsReveal = useRevealOnce({ amount: 0.25 });
  const prefersReducedMotion = useReducedMotion();

  return (
    <section id="transformations-testimonials" className="relative w-full py-16 md:py-20 bg-slate-950 text-white overflow-hidden">
      {/* Enhanced Aceternity-style gradient background */}
      <div className="pointer-events-none absolute inset-0">
        {/* Large radial gradient glow behind transformations */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(0, 0, 255, 0.15) 0%, rgba(0, 0, 255, 0.05) 40%, transparent 70%)',
            filter: 'blur(80px)',
          }}
          aria-hidden="true"
        />
        {/* Additional accent glows */}
        <div className="pointer-events-none absolute top-1/4 left-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" aria-hidden="true" />
        <div className="pointer-events-none absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-50" aria-hidden="true" />
      </div>

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(148 163 184) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
        aria-hidden="true"
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Heading and intro */}
          <motion.div
            ref={headingReveal.ref}
            className="text-center mb-12 md:mb-16"
            variants={prefersReducedMotion ? {} : gridContainerVariants}
            initial="hidden"
            animate={headingReveal.controls}
          >
            <motion.h2
              variants={prefersReducedMotion ? {} : getGridItemVariants(0)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 text-white"
            >
              From "nice idea" to working revenue engine.
            </motion.h2>
            <motion.p
              variants={prefersReducedMotion ? {} : getGridItemVariants(1)}
              className="text-lg md:text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed"
            >
              A few anonymized examples of the kinds of service businesses Attain Domains is built for.
            </motion.p>
          </motion.div>

          {/* Transformations grid - Bento layout with MagicCard */}
          <motion.div
            ref={transformationsReveal.ref}
            variants={prefersReducedMotion ? {} : gridContainerVariants}
            initial="hidden"
            animate={transformationsReveal.controls}
          >
            <BentoGrid className="grid-cols-1 md:grid-cols-2">
              {TRANSFORMATIONS.map((transformation, index) => (
                <BentoGridItem key={transformation.id} className={transformation.span}>
                  <motion.div
                    variants={prefersReducedMotion ? {} : getGridItemVariants(index)}
                    className="h-full"
                  >
                    <MagicCard
                      className="h-full bg-slate-900 border-slate-800"
                      containerClassName="h-full"
                      glowColor="rgba(0, 0, 255, 0.2)"
                      borderGlowColor="rgba(0, 0, 255, 0.5)"
                    >
                      <div className="p-6 md:p-8 h-full flex flex-col">
                        <h3 className="text-xl md:text-2xl font-bold mb-6 text-white">
                          {transformation.title}
                        </h3>
                        <div className="space-y-6 flex-1">
                          <div>
                            <p className="text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wide">
                              Before
                            </p>
                            <p className="text-base md:text-lg text-slate-300 leading-relaxed">
                              {transformation.before}
                            </p>
                          </div>
                          <div className="pt-4 border-t border-slate-800">
                            <p className="text-sm font-semibold text-[#0000FF] mb-3 uppercase tracking-wide">
                              After
                            </p>
                            <p className="text-base md:text-lg text-white leading-relaxed">
                              {transformation.after}
                            </p>
                          </div>
                        </div>
                      </div>
                    </MagicCard>
                  </motion.div>
                </BentoGridItem>
              ))}
            </BentoGrid>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
